import React, { useEffect, useState } from 'react'
import { Form, Input, Button, Upload, Avatar, Space, Typography, message, Skeleton } from 'antd'
import { UploadOutlined, UserOutlined } from '@ant-design/icons'
import { platformSettingsService, type PlatformSetting } from '../../../shared/api/services/platform-settings.service'

const { Title } = Typography

// Map between backend snake_case keys and frontend form field names
const SETTING_KEYS = [
    'platform_name',
    'platform_logo_url',
    'support_email',
    'support_phone',
    'support_whatsapp',
    'support_hours',
    'website_url',
] as const

interface GeneralSettingsProps {
    onSave: (values: any) => void
}

export const GeneralSettings: React.FC<GeneralSettingsProps> = ({ onSave }) => {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [logoUrl, setLogoUrl] = useState<string>('')

    useEffect(() => {
        fetchSettings()
    }, [])

    const fetchSettings = async () => {
        try {
            setLoading(true)
            const res = await platformSettingsService.getSettings()
            const allSettings = res.data || {}

            // Flatten grouped settings to find general-related ones
            const flatSettings: Record<string, string> = {}
            Object.values(allSettings).forEach((group: PlatformSetting[]) => {
                group.forEach((s) => {
                    if ((SETTING_KEYS as readonly string[]).includes(s.key)) {
                        flatSettings[s.key] = String(s.value ?? '')
                    }
                })
            })

            setLogoUrl(flatSettings.platform_logo_url || '')

            form.setFieldsValue({
                platform_name: flatSettings.platform_name || 'BuildBid Marketplace',
                support_email: flatSettings.support_email || 'support@buildwise.qzz.io',
                support_phone: flatSettings.support_phone || '+91 80 4567 8900',
                support_whatsapp: flatSettings.support_whatsapp || '918045678900',
                support_hours: flatSettings.support_hours || 'Mon–Sat, 9AM–8PM',
                website_url: flatSettings.website_url || 'https://buildwise.qzz.io',
            })
        } catch (error) {
            console.error('Failed to load general settings', error)
            message.error('Failed to load general settings')
            // Fallback defaults
            form.setFieldsValue({
                platform_name: 'BuildBid Marketplace',
                support_email: 'support@buildwise.qzz.io',
                support_phone: '+91 80 4567 8900',
                support_whatsapp: '918045678900',
                support_hours: 'Mon–Sat, 9AM–8PM',
                website_url: 'https://buildwise.qzz.io',
            })
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async (values: any) => {
        try {
            setSaving(true)

            // Send settings with backend snake_case keys
            await platformSettingsService.updateSettings({
                platform_name: values.platform_name,
                platform_logo_url: logoUrl,
                support_email: values.support_email,
                support_phone: values.support_phone,
                support_whatsapp: values.support_whatsapp,
                support_hours: values.support_hours,
                website_url: values.website_url,
            })

            message.success('General settings saved successfully!')
            onSave(values)
        } catch (error) {
            console.error('Failed to save settings', error)
            message.error('Failed to save settings')
        } finally {
            setSaving(false)
        }
    }

    const handleLogoUpload = (info: any) => {
        const file = info.file?.originFileObj || info.file
        if (file) {
            // Convert to base64 data URL for preview and storage
            const reader = new FileReader()
            reader.onload = (e) => {
                const dataUrl = e.target?.result as string
                setLogoUrl(dataUrl)
            }
            reader.readAsDataURL(file)
        }
        return false // prevent default upload behaviour
    }

    if (loading) {
        return <Skeleton active paragraph={{ rows: 6 }} />
    }

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
        >
            <Title level={4}>Platform Identity</Title>
            <Space align="start" size="large" style={{ marginBottom: 24 }}>
                <Avatar
                    size={64}
                    icon={<UserOutlined />}
                    src={logoUrl || undefined}
                />
                <Upload
                    showUploadList={false}
                    beforeUpload={() => false}
                    onChange={handleLogoUpload}
                    accept="image/*"
                >
                    <Button icon={<UploadOutlined />}>Change Logo</Button>
                </Upload>
            </Space>

            <Form.Item label="Platform Name" name="platform_name" rules={[{ required: true, message: 'Platform name is required' }]}>
                <Input />
            </Form.Item>


            <Title level={4}>Contact & Support</Title>
            <Form.Item label="Support Email" name="support_email" rules={[{ required: true, type: 'email', message: 'A valid email is required' }]}>
                <Input placeholder="support@buildwise.qzz.io" />
            </Form.Item>

            <Form.Item label="Support Phone" name="support_phone" rules={[{ required: true, message: 'Support phone is required' }]}>
                <Input placeholder="+91 80 4567 8900" />
            </Form.Item>

            <Form.Item label="Support WhatsApp Number (with country code)" name="support_whatsapp">
                <Input placeholder="918045678900" />
            </Form.Item>

            <Form.Item label="Support Working Hours" name="support_hours">
                <Input placeholder="Mon–Sat, 9AM–8PM" />
            </Form.Item>

            <Form.Item label="Website URL" name="website_url">
                <Input placeholder="https://buildwise.qzz.io" />
            </Form.Item>

            <Form.Item>
                <Button type="primary" htmlType="submit" loading={saving}>
                    Save Changes
                </Button>
            </Form.Item>
        </Form>
    )
}
